import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import solucoes from '@/mocks/solucoes.json';
import PacoteAcademiaForm from '@/components/feature/PacoteAcademiaForm';

/* Páginas de solução (uma por caso de uso). Conteúdo em src/mocks/solucoes.json.
   O título, a descrição, o canonical, o texto estático e os dados estruturados também são
   gravados no HTML pelo scripts/discover-meta.mjs, a partir do mesmo JSON. */
type Solucao = {
  slug: string; modelo: string; img: string; title: string; description: string;
  h1: string; kicker: string; lead: string; intro: string[];
  beneficios: [string, string][]; usos: string[]; pacote: string; faq: [string, string][]; whats?: string; credito?: string;
};
const LISTA = solucoes as unknown as Solucao[];
const SITE = 'https://stibpalestrasilenciosasp.com.br';
const WHATS = 'https://wa.me/5511917776227';

export default function SolucaoPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/|\/$/g, '');
  const s = LISTA.find((x) => x.slug === slug) || LISTA[0];
  const isAcad = s.slug === 'fones-sem-fio-aulas-spinning-academias';
  const WA = s.whats ? `${WHATS}?text=${encodeURIComponent(s.whats)}` : WHATS;
  const [aberta, setAberta] = useState<number | null>(0);

  useEffect(() => {
    document.title = s.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', s.description);
    const url = `${SITE}/${s.slug}`;
    const schemas = [
      { id: 'solucao-service-schema', schema: {
        '@context': 'https://schema.org', '@type': 'Service', name: s.h1, description: s.description, url,
        image: `${SITE}/img/stib/${s.img}.webp`, areaServed: { '@type': 'Country', name: 'Brasil' },
        provider: { '@type': 'LocalBusiness', name: 'STIB Palestra Silenciosa', url: SITE, telephone: '+55-11-91777-6227' },
        offers: { '@type': 'Offer', availability: 'https://schema.org/InStock', description: 'Orçamento sob consulta. Pacotes mensais ou pacotes de eventos disponíveis.' },
      } },
      { id: 'solucao-faq-schema', schema: {
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: s.faq.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
      } },
      { id: 'solucao-breadcrumb-schema', schema: {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Aluguel de fones sem fio', item: `${SITE}/aluguel-fones-sem-fio` },
          { '@type': 'ListItem', position: 3, name: s.h1, item: url },
        ],
      } },
    ];
    schemas.forEach(({ id, schema }) => {
      document.getElementById(id)?.remove();
      const el = document.createElement('script');
      el.type = 'application/ld+json'; el.id = id; el.text = JSON.stringify(schema);
      document.head.appendChild(el);
    });
    window.scrollTo(0, 0);
    return () => schemas.forEach(({ id }) => document.getElementById(id)?.remove());
  }, [s]);

  const outras = LISTA.filter((x) => x.slug !== s.slug);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />

      <section className="relative min-h-[460px] flex items-center overflow-hidden mt-[72px]">
        <img src={`/img/stib/${s.img}.webp`} alt={`${s.h1} — participantes usando os fones LED da STIB`}
          className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/90 via-secondary/70 to-secondary/30"></div>
        <div className="absolute top-6 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <Breadcrumb variant="light" items={[{ label: 'Home', to: '/' }, { label: 'Aluguel de fones sem fio', to: '/aluguel-fones-sem-fio' }, { label: s.kicker }]} />
          </div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <p className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3">{s.kicker}</p>
          <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-5 max-w-3xl">{s.h1}</h1>
          <p className="font-body text-lg text-white/90 max-w-2xl mb-8">{s.lead}</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/orcamento" className="font-body font-semibold bg-primary text-secondary px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity">Solicitar orçamento</Link>
            <a href={WA} target="_blank" rel="noopener noreferrer" className="font-body font-semibold border border-white text-white px-7 py-3.5 rounded-full hover:bg-white hover:text-secondary transition-colors">WhatsApp (11) 91777-6227</a>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 font-body text-gray-700 dark:text-gray-300 text-lg leading-relaxed space-y-5">
          {s.intro.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </section>

      <section className="py-16 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-secondary dark:text-white text-center mb-10">Por que usar fones sem fio</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {s.beneficios.map(([t, d]) => (
              <div key={t} className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
                <h3 className="font-heading font-bold text-secondary dark:text-white mb-2">{t}</h3>
                <p className="font-body text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading font-bold text-3xl text-secondary dark:text-white mb-6">Onde usar</h2>
            <ul className="space-y-3">
              {s.usos.map((u) => (
                <li key={u} className="flex gap-3 font-body text-gray-700 dark:text-gray-300"><span className="text-primary font-bold">✓</span>{u}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-secondary text-white p-8 md:p-10">
            <p className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3">Pacotes mensais e pacotes de eventos</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl mb-4">Economia com previsibilidade</h2>
            <p className="font-body text-white/85 leading-relaxed mb-4">{s.pacote}</p>
            <ul className="font-body text-white/85 space-y-2 mb-6">
              <li>• Valor fechado: orçamento previsível para o mês ou para o calendário de eventos.</li>
              <li>• Economia em relação à contratação avulsa, evento a evento.</li>
              <li>• Fones e equipe reservados nas suas datas.</li>
            </ul>
            {isAcad && (<p className="font-body text-white font-semibold mb-6">São Paulo capital: a partir de R$ 4.900/mês + tarifas (cerca de R$ 163 por dia). Consulte as condições — mais de uma academia: condições especiais.</p>)}
            {isAcad ? (<a href="#pacote" className="inline-block font-body font-semibold bg-primary text-secondary px-6 py-3 rounded-full hover:opacity-90 transition-opacity">Quero um pacote</a>) : (<Link to="/orcamento" className="inline-block font-body font-semibold bg-primary text-secondary px-6 py-3 rounded-full hover:opacity-90 transition-opacity">Quero um pacote</Link>)}
          </div>
        </div>
      </section>

      {isAcad && <PacoteAcademiaForm servico="Contrato com academia — aulas de spinning (pacote mensal)" pagina="/fones-sem-fio-aulas-spinning-academias" />}

      <section className="py-16 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading font-bold text-3xl text-secondary dark:text-white text-center mb-3">Nossos fones</h2>
          <p className="font-body text-gray-600 dark:text-gray-300 text-center max-w-2xl mx-auto mb-10">Equipamento próprio, entregue, instalado e operado pela equipe da STIB. Mais de 5.000 fones disponíveis, bateria de 10 a 12 horas e até 10 canais simultâneos.</p>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[['/img/stib/fone-led-uhf.webp', 'Fone sem fio LED UHF', 'Anel de LED com cor personalizável que identifica o canal. O favorito para aulas, festas, feiras e grandes eventos.'],
              ['/img/stib/fone-vhf.jpg', 'Fone sem fio VHF', 'Visor de canal integrado e transmissão estável. Indicado para palestras, visitas técnicas e treinamentos.']].map(([src, nome, txt]) => (
              <div key={nome} className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
                <img src={src} alt={nome} loading="lazy" className={`w-full aspect-[16/9] ${src.includes('vhf') ? 'object-contain object-center bg-white p-4' : 'object-cover'}`} />
                <div className="p-6">
                  <h3 className="font-heading font-bold text-secondary dark:text-white mb-2">{nome}</h3>
                  <p className="font-body text-sm text-gray-600 dark:text-gray-300">{txt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading font-bold text-3xl text-secondary dark:text-white text-center mb-8">Como funciona</h2>
          <ol className="grid sm:grid-cols-2 gap-5 font-body">
            {[['Contato', 'Conte o tipo de atividade, a data, o local e o número de pessoas.'],
              ['Proposta', 'Indicamos o modelo de fone, os canais e o formato: avulso, pacote mensal ou pacote de eventos.'],
              ['Montagem', 'Nossa equipe entrega, instala e testa todo o sistema antes do início.'],
              ['Operação e recolhimento', 'Suporte técnico presencial durante o uso e recolhimento ao final.']].map(([t, d], i) => (
              <li key={t} className="rounded-2xl border border-gray-100 dark:border-gray-700 p-5">
                <span className="font-heading font-bold text-primary text-2xl">{i + 1}</span>
                <h3 className="font-heading font-bold text-secondary dark:text-white mt-1 mb-1">{t}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-16 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-heading font-bold text-3xl text-secondary dark:text-white text-center mb-8">Perguntas frequentes</h2>
          <div className="space-y-3">
            {s.faq.map(([q, a], i) => (
              <div key={q} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                <button type="button" onClick={() => setAberta(aberta === i ? null : i)} aria-expanded={aberta === i}
                  className="w-full text-left px-5 py-4 font-heading font-semibold text-secondary dark:text-white flex justify-between gap-4 cursor-pointer">
                  <span>{q}</span><span aria-hidden="true">{aberta === i ? '−' : '+'}</span>
                </button>
                <div className={aberta === i ? 'px-5 pb-4 font-body text-gray-600 dark:text-gray-300' : 'hidden'}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading font-bold text-2xl text-secondary dark:text-white mb-6">Outras soluções com fones sem fio</h2>
          <div className="flex flex-wrap gap-3">
            {outras.map((o) => (
              <Link key={o.slug} to={`/${o.slug}`} className="font-body text-sm px-4 py-2 rounded-full border border-gray-200 dark:border-gray-700 text-secondary dark:text-gray-200 hover:border-primary hover:text-primary transition-colors">{o.kicker}</Link>
            ))}
            <Link to="/aluguel-fones-sem-fio" className="font-body text-sm px-4 py-2 rounded-full border border-gray-200 dark:border-gray-700 text-secondary dark:text-gray-200 hover:border-primary hover:text-primary transition-colors">Aluguel de fones sem fio</Link>
            <Link to="/eventos" className="font-body text-sm px-4 py-2 rounded-full border border-gray-200 dark:border-gray-700 text-secondary dark:text-gray-200 hover:border-primary hover:text-primary transition-colors">Fotos de eventos reais</Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-4">Peça seu orçamento</h2>
          <p className="font-body text-white/80 mb-8">Atendemos todo o Brasil a partir da sede em São Paulo e da filial em Brasília, com equipe e equipamentos também presentes em Recife, Rio de Janeiro e Campinas.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/orcamento" className="font-body font-semibold bg-primary text-secondary px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity">Solicitar orçamento</Link>
            <a href={WA} target="_blank" rel="noopener noreferrer" className="font-body font-semibold border border-white text-white px-7 py-3.5 rounded-full hover:bg-white hover:text-secondary transition-colors">Falar no WhatsApp</a>
          </div>
        </div>
      </section>

      {s.credito && <p className="font-body text-center text-xs text-gray-400 dark:text-gray-500 px-6 py-4">{s.credito}</p>}
      <Footer />
    </div>
  );
}
